// Direct imports
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Observer } from "@babylonjs/core/Misc/observable";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Scene } from "@babylonjs/core/scene";
import { TraceBehavior } from "./tracebehavior";
import { RotateData, RotateAxis } from "../scenedata/behaviors/rotatedata";
import { pivotOnParentScale, resetTransformToIdentity } from "../utils/TransformUtils";

const DEG_TO_RAD = Math.PI / 180;

// Shared across all Rotate instances — safe because observers run synchronously.
const _tmpParentScale = new Vector3();

function resolveAxis(axis: RotateAxis): Vector3 {
    switch (axis) {
        case "x": return Vector3.Right();
        case "y": return Vector3.Up();
        case "z": return Vector3.Forward();
        case "Random": return new Vector3(
            Math.random() * 2 - 1,
            Math.random() * 2 - 1,
            Math.random() * 2 - 1,
        ).normalize();
        default: return Vector3.Up();
    }
}

function resolveRuntimeSpeed(speed: number, speedRange: number): number {
    if (speedRange <= 0) return speed;
    const offset = (Math.random() * 2 - 1) * speedRange;
    return Math.max(0, speed + offset);
}

/**
 * Rotate behavior - Continuous rotation animation
 *
 * Applies a constant rotation to the object's animation root each frame.
 * Speed is in degrees/second with optional randomization range.
 *
 * The authored scale lives on the parent transform, so a turn applied to the
 * root would land under it and shear a non-uniformly scaled object. While
 * spinning, the root is pivoted on the parent's scale (as Billboard does) so
 * the turn stays rigid.
 */
export class Rotate extends TraceBehavior<RotateData> {
    static readonly behaviorType = "Rotate" as const;

    private _renderObserver: Observer<Scene> | null = null;
    private _runtimeAxis: Vector3 = Vector3.Up();
    private _runtimeSpeed: number = 90; // degrees/sec
    /** True once this behavior has pivoted the root, so only it clears the pivot. */
    private _pivoted = false;

    protected onDetach(): void {
        this._stopRotation();
        const animRoot = this.sceneObject.animationRoot;
        if (animRoot) {
            resetTransformToIdentity(animRoot, '[Rotate]');
        }
    }

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            this._startRotation();
        } else {
            this._stopRotation();
        }
    }

    protected async applyCommit(patch: Partial<RotateData>): Promise<void> {
        const needsRestart = patch.axis !== undefined;

        // Speed-only change while already running: update in-place without resetting rotation
        if (!needsRestart && this._renderObserver && this.playMode) {
            const speed = this._resolved.speed ?? 90;
            const speedRange = this._resolved.speedRange ?? 0;
            this._runtimeSpeed = resolveRuntimeSpeed(speed, speedRange);
            return;
        }

        this._stopRotation();
        if (this.playMode) {
            this._startRotation();
        }
    }

    private _startRotation(): void {
        if (!this.scene) return;

        // Guard against duplicate observers from multiple attach calls
        if (this._renderObserver) {
            this._stopRotation();
        }

        // Take control and reset the animation root
        const animRoot = this.sceneObject.getOrCreateAnimationRoot();
        resetTransformToIdentity(animRoot, '[Rotate]');

        const axis = this._resolved.axis ?? "y";
        const speed = this._resolved.speed ?? 90;
        const speedRange = this._resolved.speedRange ?? 0;

        this._runtimeAxis = resolveAxis(axis);
        this._runtimeSpeed = resolveRuntimeSpeed(speed, speedRange);

        // Ensure quaternion rotation is initialized
        if (!animRoot.rotationQuaternion) {
            animRoot.rotationQuaternion = Quaternion.Identity();
        }

        this._renderObserver = this.scene.onBeforeRenderObservable.add(() => {
            if (!this.scene) return;
            const dt = this.scene.getEngine().getDeltaTime() / 1000;
            const angle = this._runtimeSpeed * DEG_TO_RAD * dt;
            const increment = Quaternion.RotationAxis(this._runtimeAxis, angle);
            animRoot.rotationQuaternion!.multiplyInPlace(increment);

            const parent = animRoot.parent as TransformNode | null;
            if (parent) {
                parent.getWorldMatrix().decompose(_tmpParentScale);
            } else {
                _tmpParentScale.setAll(1);
            }
            pivotOnParentScale(animRoot, _tmpParentScale);
            this._pivoted = true;
        });
    }

    private _stopRotation(): void {
        if (this._renderObserver && this.scene) {
            this.scene.onBeforeRenderObservable.remove(this._renderObserver);
            this._renderObserver = null;
        }
        const animRoot = this.sceneObject.animationRoot;
        if (animRoot) {
            resetTransformToIdentity(animRoot, '[Rotate]');
            if (this._pivoted) animRoot.setPivotMatrix(Matrix.IdentityReadOnly);
        }
        this._pivoted = false;
    }
}
